import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Eye, EyeOff, MapPin, FileSearch, Star, MoreVertical,
  LandPlot, Search, Download, Loader2, AlertCircle,
} from 'lucide-react';
import { BulkActionBar } from './BulkActionBar';
import { logAdminAction } from '@/lib/auditLog';

const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
};

type BulkAction = 'approve' | 'reject' | 'feature' | 'unfeature';
type RowResult = { status: 'pending' | 'success' | 'error'; message?: string };

const bulkLabels: Record<BulkAction, { verb: string; pastVerb: string; description: string }> = {
  approve: { verb: 'Approve', pastVerb: 'approved', description: 'Mark these plots as approved and make them live on the site.' },
  reject: { verb: 'Reject', pastVerb: 'rejected', description: 'Reject these plots and remove them from the public site.' },
  feature: { verb: 'Feature', pastVerb: 'featured', description: 'Highlight these plots as featured on the platform.' },
  unfeature: { verb: 'Unfeature', pastVerb: 'unfeatured', description: 'Remove the featured highlight from these plots.' },
};

export function PlotApprovals() {
  const queryClient = useQueryClient();
  const [selectedPlot, setSelectedPlot] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [markFeatured, setMarkFeatured] = useState(false);
  const [filterTab, setFilterTab] = useState('pending_review');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelected = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelected = () => setSelectedIds(new Set());

  // Search & filters
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [purposeFilter, setPurposeFilter] = useState<string>('all');

  // Bulk confirm + per-row progress
  const [pendingBulk, setPendingBulk] = useState<{ action: BulkAction; ids: string[] } | null>(null);
  const [rowResults, setRowResults] = useState<Record<string, RowResult>>({});
  const [bulkRunning, setBulkRunning] = useState(false);

  const { data: plots, isLoading } = useQuery({
    queryKey: ['plots-for-approval'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plots')
        .select('*, plot_images(url, is_primary)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ plotId, status, notes, featured }: { plotId: string; status: string; notes: string; featured?: boolean }) => {
      const update: any = { approval_status: status, reviewer_notes: notes };
      if (status === 'approved') {
        update.status = 'active';
        if (featured) update.is_featured = true;
      }
      const { error } = await supabase.from('plots').update(update).eq('id', plotId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['plots-for-approval'] });
      toast.success(vars.status === 'approved' ? 'Plot approved!' : 'Plot rejected.');
      setSelectedPlot(null);
      setReviewNotes('');
      setMarkFeatured(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const quickActionMutation = useMutation({
    mutationFn: async ({ plotId, updates }: { plotId: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('plots').update(updates as any).eq('id', plotId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plots-for-approval'] });
      toast.success('Plot updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Per-row bulk runner: updates each plot individually so we can show success/failure per row
  const runBulk = async (action: BulkAction, ids: string[]) => {
    if (!ids.length) return;
    setBulkRunning(true);

    const update: any = action === 'approve' ? { approval_status: 'approved', status: 'active' }
      : action === 'reject' ? { approval_status: 'rejected', status: 'draft' }
      : action === 'feature' ? { is_featured: true }
      : { is_featured: false };

    // Initialize all rows as pending
    setRowResults(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = { status: 'pending' }; });
      return next;
    });

    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      const { error } = await supabase.from('plots').update(update).eq('id', id);
      if (error) {
        failCount++;
        setRowResults(prev => ({ ...prev, [id]: { status: 'error', message: error.message } }));
      } else {
        successCount++;
        setRowResults(prev => ({ ...prev, [id]: { status: 'success' } }));
      }
    }

    await logAdminAction({
      action: action === 'approve' ? 'bulk_approve' : action === 'reject' ? 'bulk_reject' : action === 'feature' ? 'bulk_feature' : 'bulk_unfeature',
      entity_type: 'plot',
      metadata: { ids, count: ids.length, success: successCount, failed: failCount },
    });

    queryClient.invalidateQueries({ queryKey: ['plots-for-approval'] });

    if (failCount === 0) {
      toast.success(`${successCount} plots ${bulkLabels[action].pastVerb}`);
    } else if (successCount === 0) {
      toast.error(`All ${failCount} plots failed to ${action}`);
    } else {
      toast.warning(`${successCount} ${bulkLabels[action].pastVerb}, ${failCount} failed`);
    }

    setBulkRunning(false);
    clearSelected();
    setPendingBulk(null);
    // Auto-clear row result badges after a few seconds
    setTimeout(() => setRowResults({}), 6000);
  };

  const requestBulk = (action: BulkAction) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setPendingBulk({ action, ids });
  };

  const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

  // Distinct dropdown values
  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    plots?.forEach((p: any) => p.city && set.add(p.city));
    return Array.from(set).sort();
  }, [plots]);

  const purposeOptions = useMemo(() => {
    const set = new Set<string>();
    plots?.forEach((p: any) => p.plot_purpose && set.add(p.plot_purpose));
    return Array.from(set).sort();
  }, [plots]);

  const filtered = useMemo(() => {
    if (!plots) return [];
    const q = searchQuery.trim().toLowerCase();
    return plots.filter((p: any) => {
      if (filterTab !== 'all' && p.approval_status !== filterTab) return false;
      if (cityFilter !== 'all' && p.city !== cityFilter) return false;
      if (purposeFilter !== 'all' && p.plot_purpose !== purposeFilter) return false;
      if (q) {
        const hay = [
          getName(p.title),
          p.reference_code,
          p.city,
          p.sub_city,
          p.woreda,
          p.address,
          p.contact_person,
          p.contact_phone,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [plots, filterTab, cityFilter, purposeFilter, searchQuery]);

  const pendingCount = plots?.filter((p: any) => p.approval_status === 'pending_review').length || 0;

  const exportCsv = () => {
    const rows = selectedIds.size > 0
      ? (plots || []).filter((p: any) => selectedIds.has(p.id))
      : filtered;

    if (!rows.length) {
      toast.error('Nothing to export');
      return;
    }

    const headers = [
      'Reference Code', 'Title', 'Approval Status', 'Status', 'Featured',
      'City', 'Sub City', 'Woreda', 'Address',
      'Plot Purpose', 'Listing Type', 'Price', 'Currency',
      'Area (sqm)', 'Road Width (ft)', 'Facing',
      'Contact Person', 'Contact Phone', 'Contact Email',
      'Created At', 'Updated At',
    ];

    const escape = (val: any) => {
      if (val == null) return '';
      const s = String(val).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };

    const lines = [headers.join(',')];
    rows.forEach((p: any) => {
      lines.push([
        p.reference_code, getName(p.title), p.approval_status, p.status, p.is_featured ? 'Yes' : 'No',
        p.city, p.sub_city, p.woreda, p.address,
        p.plot_purpose, p.listing_type, p.price, p.currency,
        p.area_sqm, p.road_width_ft, p.facing_direction,
        p.contact_person, p.contact_phone, p.contact_email,
        p.created_at, p.updated_at,
      ].map(escape).join(','));
    });

    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plots-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} plot${rows.length === 1 ? '' : 's'}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCityFilter('all');
    setPurposeFilter('all');
  };
  const filtersActive = searchQuery || cityFilter !== 'all' || purposeFilter !== 'all';

  if (isLoading) return <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <LandPlot className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-bold">Plot / Land Approvals</h2>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && <Badge className="bg-yellow-500 text-white">{pendingCount} Pending</Badge>}
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="h-4 w-4 mr-1.5" />
            Export CSV {selectedIds.size > 0 ? `(${selectedIds.size})` : `(${filtered.length})`}
          </Button>
        </div>
      </div>

      <Tabs value={filterTab} onValueChange={setFilterTab}>
        <TabsList>
          <TabsTrigger value="all">All ({plots?.length || 0})</TabsTrigger>
          <TabsTrigger value="pending_review">Pending ({pendingCount})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({plots?.filter((p: any) => p.approval_status === 'approved').length || 0})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({plots?.filter((p: any) => p.approval_status === 'rejected').length || 0})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, reference code, city, contact…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="md:w-48"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {cityOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={purposeFilter} onValueChange={setPurposeFilter}>
          <SelectTrigger className="md:w-48"><SelectValue placeholder="Plot purpose" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All purposes</SelectItem>
            {purposeOptions.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
        )}
      </div>

      {!filtered.length ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <FileSearch className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>{filtersActive ? 'No plots match your filters' : 'No plots in this category'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          <BulkActionBar
            selectedCount={selectedIds.size}
            onClear={clearSelected}
            onApprove={() => requestBulk('approve')}
            onReject={() => requestBulk('reject')}
            onFeature={() => requestBulk('feature')}
            onUnfeature={() => requestBulk('unfeature')}
            pending={bulkRunning}
          />
          {filtered.map((plot: any) => {
            const title = getName(plot.title);
            const cfg = statusConfig[plot.approval_status] || statusConfig.draft;
            const img = plot.plot_images?.find((i: any) => i.is_primary)?.url || plot.plot_images?.[0]?.url;
            const result = rowResults[plot.id];
            return (
              <Card key={plot.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={selectedIds.has(plot.id)}
                      onCheckedChange={() => toggleSelected(plot.id)}
                      aria-label="Select plot"
                    />
                    <div className="h-14 w-14 rounded-lg bg-muted overflow-hidden shrink-0">
                      {img ? <img src={img} alt={title} className="h-full w-full object-cover" /> : <LandPlot className="h-6 w-6 text-muted-foreground m-auto mt-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{title}</span>
                        {plot.is_featured && <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {plot.city}{plot.sub_city ? `, ${plot.sub_city}` : ''} • {plot.plot_purpose} • {plot.listing_type}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {plot.price?.toLocaleString()} {plot.currency} • {plot.area_sqm || 0} m²
                        {plot.road_width_ft ? ` • ${plot.road_width_ft}ft road` : ''}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={cfg.color}>{cfg.label}</Badge>
                      {result?.status === 'pending' && (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <Loader2 className="h-3 w-3 animate-spin" /> Updating
                        </Badge>
                      )}
                      {result?.status === 'success' && (
                        <Badge className="bg-green-100 text-green-800 gap-1 text-[10px]">
                          <CheckCircle2 className="h-3 w-3" /> Updated
                        </Badge>
                      )}
                      {result?.status === 'error' && (
                        <Badge className="bg-red-100 text-red-800 gap-1 text-[10px]" title={result.message}>
                          <AlertCircle className="h-3 w-3" /> Failed
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => {
                        setSelectedPlot(plot);
                        setReviewNotes(plot.reviewer_notes || '');
                        setMarkFeatured(plot.is_featured || false);
                      }}>
                        <Eye className="h-4 w-4 mr-1" /> Review
                      </Button>
                      {plot.approval_status === 'approved' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {plot.status === 'active' ? (
                              <DropdownMenuItem onClick={() => quickActionMutation.mutate({ plotId: plot.id, updates: { status: 'draft' } })}>
                                <EyeOff className="h-4 w-4 mr-2" /> Hide from Site
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => quickActionMutation.mutate({ plotId: plot.id, updates: { status: 'active' } })}>
                                <Eye className="h-4 w-4 mr-2" /> Show on Site
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => quickActionMutation.mutate({ plotId: plot.id, updates: { is_featured: !plot.is_featured } })}>
                              <Star className="h-4 w-4 mr-2" /> {plot.is_featured ? 'Remove Featured' : 'Mark Featured'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => quickActionMutation.mutate({ plotId: plot.id, updates: { approval_status: 'rejected', status: 'draft' } })}>
                              <XCircle className="h-4 w-4 mr-2" /> Revoke Approval
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedPlot} onOpenChange={() => setSelectedPlot(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Review: {selectedPlot && getName(selectedPlot.title)}</DialogTitle></DialogHeader>
          {selectedPlot && (
            <div className="space-y-4">
              <div className="text-sm space-y-1.5 bg-muted/50 rounded-lg p-3">
                <div><strong>Purpose:</strong> {selectedPlot.plot_purpose} ({selectedPlot.listing_type})</div>
                <div><strong>Location:</strong> {selectedPlot.city}{selectedPlot.sub_city ? `, ${selectedPlot.sub_city}` : ''}{selectedPlot.woreda ? `, ${selectedPlot.woreda}` : ''}</div>
                <div><strong>Price:</strong> {selectedPlot.price?.toLocaleString()} {selectedPlot.currency}</div>
                <div><strong>Area:</strong> {selectedPlot.area_sqm || 'N/A'} m² ({selectedPlot.area_sqm ? (selectedPlot.area_sqm * 10.764).toFixed(0) : 'N/A'} sqft)</div>
                <div className="flex gap-4">
                  {selectedPlot.road_width_ft && <span><strong>Road:</strong> {selectedPlot.road_width_ft} ft</span>}
                  {selectedPlot.facing_direction && <span><strong>Facing:</strong> {selectedPlot.facing_direction.replace('_', ' ')}</span>}
                </div>
                <div className="flex gap-3 text-xs">
                  {selectedPlot.is_corner && <Badge variant="outline">Corner Plot</Badge>}
                  {selectedPlot.is_park_face && <Badge variant="outline">Park Face</Badge>}
                  {selectedPlot.is_main_road && <Badge variant="outline">Main Road</Badge>}
                </div>
                {selectedPlot.contact_person && <div><strong>Contact:</strong> {selectedPlot.contact_person} {selectedPlot.contact_phone && `• ${selectedPlot.contact_phone}`}</div>}
              </div>

              {selectedPlot.plot_images?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {selectedPlot.plot_images.map((img: any, i: number) => (
                    <img key={i} src={img.url} alt="" className="h-16 w-20 rounded object-cover shrink-0" />
                  ))}
                </div>
              )}

              <Textarea placeholder="Review notes..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3} />

              <div className="flex items-center gap-2">
                <Switch checked={markFeatured} onCheckedChange={setMarkFeatured} id="plot-featured-toggle" />
                <Label htmlFor="plot-featured-toggle" className="flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5" /> Mark as Featured
                </Label>
              </div>

              <DialogFooter className="gap-2">
                {selectedPlot.approval_status === 'pending_review' ? (
                  <>
                    <Button variant="destructive" onClick={() => reviewMutation.mutate({ plotId: selectedPlot.id, status: 'rejected', notes: reviewNotes })} disabled={reviewMutation.isPending}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => reviewMutation.mutate({ plotId: selectedPlot.id, status: 'approved', notes: reviewNotes, featured: markFeatured })} disabled={reviewMutation.isPending}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => {
                    quickActionMutation.mutate({ plotId: selectedPlot.id, updates: { reviewer_notes: reviewNotes, is_featured: markFeatured } });
                    setSelectedPlot(null);
                  }}>
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk action confirmation */}
      <Dialog open={!!pendingBulk} onOpenChange={(o) => { if (!o && !bulkRunning) setPendingBulk(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-primary" />
              Confirm bulk {pendingBulk?.action}
            </DialogTitle>
            <DialogDescription>
              {pendingBulk && (
                <>
                  You are about to <strong>{bulkLabels[pendingBulk.action].verb.toLowerCase()}</strong>{' '}
                  <strong>{pendingBulk.ids.length}</strong> plot{pendingBulk.ids.length === 1 ? '' : 's'}.{' '}
                  {bulkLabels[pendingBulk.action].description}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingBulk(null)} disabled={bulkRunning}>
              Cancel
            </Button>
            <Button
              variant={pendingBulk?.action === 'reject' ? 'destructive' : 'default'}
              onClick={() => pendingBulk && runBulk(pendingBulk.action, pendingBulk.ids)}
              disabled={bulkRunning}
            >
              {bulkRunning && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {pendingBulk ? `${bulkLabels[pendingBulk.action].verb} ${pendingBulk.ids.length}` : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
